import { useState, useRef, ComponentProps, useEffect } from "react";

// Hooks
import { useAppDispatch } from "app/hooks";
import useAuth from "utils/hooks/useAuth";
import { useNavigate } from "react-router-dom";

// Components
import CreateYourProfile from "./CreateYourProfile/CreateYourProfile";
import IsThisYou from "./IsThisYou/IsThisYou";
import LetsGetStarted from "./LetsGetStarted/LetsGetStarted";
import WorkDetails from "./WorkDetails/WorkDetails";

// Store
import { fetchSession } from "store/slice/auth";

// Services
import {
    fetchPersonWorkHistory,
    identifyPersonService,
    linkCurrentUserToPersonService,
    createPersonService,
    updateWorkHistoryService,
    fetchPersonUserId,
} from "services";

// Utils
import { generateID, isLinkedValid, randomNumber, sleep, sortWorkHistoryAddFromTo } from "utils";
import { unwrapResult } from "@reduxjs/toolkit";
import { urlPaths } from "enums/urlPaths";

// Types
import { Step, StepConfig, DatePicker, WHEntry, EntryError } from "./SetupWizard.types";
import {
    convertWorkHistorytoWHEntry,
    updateWorkHistory,
    areAllWhEntriesValid,
    convertWorkHistoryBeforeSave,
} from "./SetupWizard.functions";

export const defaultWHEntry: WHEntry = {
    id: 0,
    workPosition: null,
    companyName: null,
    startDate: { value: null, isOpen: false },
    endDate: { value: null, isOpen: false, isPresent: false },
};

const useSetupWizard = (): [
    {
        step: StepConfig;
        steps: Step[][];
        shouldShowLoading: boolean;
        shouldDisableButton: boolean;
    },
    {
        handleMove: (stepDirection: "backStep" | "nextStep") => Promise<void>;
        getFieldsValidation: () => boolean;
    }
] => {
    const dispatch = useAppDispatch();
    const { person_id } = useAuth();
    const navigate = useNavigate();

    const [step, setStep] = useState({ step: 0, subStep: 0 });
    const [shouldShowLoading, setShouldShowLoading] = useState(false);
    const [whEntries, setWHEntries] = useState<WHEntry[]>([]);
    const [shouldDisableButton, setShouldDisableButton] = useState(false);
    const [render, setRender] = useState(true);
    const [createProfileAlert, setCreateProfileAlert] = useState(false);
    const [linkedInConfirmation, setLinkedInConfirmation] = useState(false);

    // Redirect to Dashboard if no personId on Session
    useEffect(() => {
        if (person_id) {
            navigate(urlPaths.PaceLabsDashboard);
        }
    }, [person_id, navigate]);

    // Lets Get Started
    const [selectedWorkPosition, setSelectedWorkPosition] =
        useState<ComponentProps<typeof LetsGetStarted>["selectedWorkPosition"]>(null);
    const [selectedCompany, setSelectedCompany] =
        useState<ComponentProps<typeof LetsGetStarted>["selectedCompany"]>(null);
    const [selectedCompanyWebsite, setSelectedCompanyWebsite] = useState("");
    const [selectedLinkedinId, setSelectedLinkedinId] = useState("");

    // Is this you
    const [workHistoryData, setworkHistoryData] = useState<ComponentProps<typeof IsThisYou>["workHistoryData"]>([]);
    const [foundProfile, setFoundProfile] = useState(0);

    // Work Details
    const [startDate, setStartDate] = useState<DatePicker>({
        value: new Date(),
        isOpen: false,
    });
    const [endDate, setEndDate] = useState<DatePicker>({
        value: null,
        isOpen: false,
        isPresent: true,
    });

    const [errors, setErrors] = useState({ company: false, workPosition: false, linkedIn: false });
    const [entryErrors, setEntryErrors] = useState<EntryError[]>([]);

    const wrapperRef = useRef<HTMLDivElement>(null);

    const handleShowCreateYourProfileAlert = (value: boolean) => {
        setCreateProfileAlert(value);
    };

    const handleFetchMatchedW = async () => {
        const payLoad = {
            ...(isLinkedValid(selectedLinkedinId) ? { linkedin_identifier: selectedLinkedinId } : {}),
            ...(selectedCompany !== null && typeof selectedCompany === "string"
                ? { current_company_name: selectedCompany }
                : { current_company_name: selectedCompany?.name }),
            ...(selectedCompany !== null && typeof selectedCompany !== "string"
                ? { current_company_id: selectedCompany?.id }
                : {}),
            ...(selectedWorkPosition !== null && typeof selectedWorkPosition === "string"
                ? { current_job_title: selectedWorkPosition }
                : { current_job_title: selectedWorkPosition?.name }),
        };

        let personId: number | undefined;

        try {
            const { data } = await identifyPersonService(payLoad);

            if (data?.matches.length) {
                const result = await fetchPersonUserId(data.matches[0].person_id);
                const isClaimedWorkHistory = Boolean(result.data.user_id);

                if (!isClaimedWorkHistory) {
                    personId = data.matches[0].person_id;
                    setFoundProfile(personId);
                }
            }
        } catch (error) {
            setFoundProfile(0);
            console.error(error);
        }

        if (personId) {
            try {
                const { data } = await fetchPersonWorkHistory(personId);
                const sortedWorkhistory = sortWorkHistoryAddFromTo(data, "tenure_to");
                if (sortedWorkhistory?.length) setworkHistoryData(sortedWorkhistory);
            } catch (error) {
                console.error(error);
            }
        } else {
            setWHEntries([
                {
                    id: generateID(),
                    workPosition: selectedWorkPosition,
                    companyName: selectedCompany,
                    startDate: { ...startDate, value: startDate.value },
                    endDate: { ...endDate, value: endDate.value, isPresent: Boolean(endDate.isPresent) },
                },
            ]);
        }

        setShouldShowLoading(false);
        setStep({ step: 1, subStep: Boolean(personId) ? 0 : 1 });
    };

    const handleSessionRequest = async () => {
        let hasPersonId = false;

        try {
            const response = unwrapResult(await dispatch(fetchSession()));
            hasPersonId = Boolean(response?.authn?.person?.id);
        } catch (error) {
            console.error(error);
        }

        return hasPersonId;
    };

    const handleFinish = async () => {
        let responseStatus = 0;
        const work_history = convertWorkHistoryBeforeSave(workHistoryData);

        if (foundProfile) {
            setShouldDisableButton(true);

            try {
                const { data } = await linkCurrentUserToPersonService(foundProfile);
                if (data.person_id) {
                    const response = await updateWorkHistoryService({
                        person_id: data.person_id,
                        work_history,
                    });

                    responseStatus = response.status;
                }
            } catch (error) {
                console.error(error);
                setShouldDisableButton(false);
            }
        } else {
            setShouldDisableButton(true);
            try {
                const response = await createPersonService({
                    ...(isLinkedValid(selectedLinkedinId) ? { linkedin_id: selectedLinkedinId } : {}),
                    work_history,
                });

                responseStatus = response.status;
            } catch (error) {
                console.error(error);
                setShouldDisableButton(false);
            }
        }

        if (responseStatus === 200) {
            let hasPersonId;

            // Make sure we get a person_id on the session if not, at the very end, at least re-enable the button
            await setTimeout(async () => {
                hasPersonId = await handleSessionRequest();
            }, 2000);

            // If not, try again or at the very end re-enable the button
            if (!hasPersonId) {
                setTimeout(async () => {
                    hasPersonId = await handleSessionRequest();
                    if (!hasPersonId) {
                        setShouldDisableButton(false);
                    }
                }, 4000);
            }
        }
    };

    const handleMove = async (stepDirection: "backStep" | "nextStep") => {
        if (step.step === 0 && step.subStep === 0 && stepDirection === "nextStep") {
            if (getFieldsValidation()) {
                return;
            }
            setLinkedInConfirmation(false);
        }
        // Work Details - Click on "Create"
        if (step.step === 0 && step.subStep === 1 && stepDirection === "nextStep") {
            setWHEntries([]);
            setShouldShowLoading(true);
            await sleep(randomNumber(5000, 12000));
            await handleFetchMatchedW();
            return;
        }

        // Is this you - Click on "Not me"
        if (step.step === 1 && step.subStep === 0 && stepDirection === "backStep") {
            setFoundProfile(0);
            setWHEntries([
                {
                    id: generateID(),
                    workPosition: selectedWorkPosition,
                    companyName: selectedCompany,
                    startDate: { ...startDate, value: startDate.value },
                    endDate: { ...endDate, value: endDate.value, isPresent: Boolean(endDate.isPresent) },
                },
            ]);
        }

        // Summary - Click on "Edit"
        if (
            (step.step === 2 && step.subStep === 0 && stepDirection === "backStep") ||
            (step.step === 1 && step.subStep === 0 && stepDirection === "nextStep")
        ) {
            const convertedWH = convertWorkHistorytoWHEntry(workHistoryData);
            setWHEntries(convertedWH);
        }

        // Create you profile - Click on "Back"
        if (step.step === 1 && step.subStep === 1 && stepDirection === "backStep") {
            setEntryErrors([]);
        }

        // Create you profile - Click on "Finish"
        if (step.step === 1 && step.subStep === 1 && stepDirection === "nextStep") {
            if (getFieldsValidation()) {
                handleShowCreateYourProfileAlert(true);
                return;
            }
            setEntryErrors([]);
            const updatedWh = updateWorkHistory(workHistoryData, whEntries);
            const sortedWorkhistory = sortWorkHistoryAddFromTo(updatedWh, "tenure_to");
            if (sortedWorkhistory?.length) setworkHistoryData(sortedWorkhistory);
        }

        // Summary - Click on "Finish"
        if (step.step === 2 && step.subStep === 0 && stepDirection === "nextStep") {
            await handleFinish();
            return;
        }

        setStep(steps[step.step][step.subStep][stepDirection]);
    };

    const onClickAddRoleCompany = () => {
        setWHEntries([...whEntries, { ...defaultWHEntry, id: generateID() }]);
        setTimeout(() => {
            if (wrapperRef.current) {
                wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight;
            }
        }, 50);
        setRender(!render);
    };

    const getFieldsValidation = () => {
        let isNotValid = false;
        const newErrors = { company: false, workPosition: false, linkedIn: false, startDate: false, endDate: false };

        const newEntryErrors = [] as EntryError[];

        // Work details
        if (step.step === 0 && step.subStep === 0) {
            if (!selectedCompany && !selectedWorkPosition) {
                isNotValid = true;
                newErrors.company = true;
                newErrors.workPosition = true;
            } else {
                if (!selectedCompany) {
                    isNotValid = true;
                    newErrors.company = true;
                }

                if (!selectedWorkPosition) {
                    isNotValid = true;
                    newErrors.workPosition = true;
                }
            }

            if (!isLinkedValid(selectedLinkedinId) && !linkedInConfirmation) {
                isNotValid = true;
                newErrors.linkedIn = true;

                if (!linkedInConfirmation) {
                    setLinkedInConfirmation(true);
                }
            }
        }

        // Create your profile
        if (step.step === 1 && step.subStep === 1) {
            const allWHEntriesValid = areAllWhEntriesValid(whEntries);

            if (!allWHEntriesValid) {
                whEntries.forEach((whEntry, index) => {
                    const entryErrors = {
                        id: whEntry.id,
                        company: !whEntry.companyName,
                        workPosition: !whEntry.workPosition,
                        startDate: !whEntry.startDate.value,
                        endDate: !(whEntry.endDate.value || whEntry.endDate.isPresent),
                    };

                    newEntryErrors.push(entryErrors);

                    if (Object.values(entryErrors).some((error) => error)) {
                        isNotValid = true;
                    }
                });
            }
        }

        setErrors(newErrors);
        setEntryErrors(newEntryErrors);
        return isNotValid;
    };

    const steps: Step[][] = [
        [
            {
                component: (
                    <LetsGetStarted
                        selectedWorkPosition={selectedWorkPosition}
                        setSelectedWorkPosition={setSelectedWorkPosition}
                        selectedCompany={selectedCompany}
                        setSelectedCompany={setSelectedCompany}
                        selectedCompanyWebsite={selectedCompanyWebsite}
                        setSelectedCompanyWebsite={setSelectedCompanyWebsite}
                        selectedLinkedinId={selectedLinkedinId}
                        setSelectedLinkedinId={setSelectedLinkedinId}
                        errors={errors}
                        setErrors={setErrors}
                    />
                ),
                nextTitle: "wizard.step0.next.subStep0",
                backTitle: "wizard.step0.back.subStep0",
                nextStep: { step: 0, subStep: 1 },
                backStep: { step: 0, subStep: 0 },
            },
            {
                component: (
                    <WorkDetails
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                    />
                ),
                nextTitle: "wizard.step0.next.subStep1",
                backTitle: "wizard.step0.back.subStep1",
                nextStep: { step: 1, subStep: Boolean(foundProfile) ? 1 : 0 },
                backStep: { step: 0, subStep: 0 },
            },
        ],
        [
            {
                component: <IsThisYou workHistoryData={workHistoryData} />,
                nextTitle: "wizard.step1.next.subStep0-a",
                backTitle: "wizard.step1.back.subStep0-a",
                nextStep: { step: 1, subStep: 1 },
                backStep: { step: 1, subStep: 1 },
            },
            {
                component: (
                    <CreateYourProfile
                        whEntries={whEntries}
                        onClickAdd={onClickAddRoleCompany}
                        setWHEntries={setWHEntries}
                        wrapperRef={wrapperRef}
                        profileExists={Boolean(foundProfile)}
                        entryErrors={entryErrors}
                        setEntryErrors={setEntryErrors}
                        showAlert={createProfileAlert}
                        setShowAlert={handleShowCreateYourProfileAlert}
                    />
                ),
                nextTitle: "wizard.step1.next.subStep0-b",
                backTitle: "wizard.step1.back.subStep0-b",
                nextStep: { step: 2, subStep: 0 },
                backStep: { step: 0, subStep: 1 },
            },
        ],
        [
            {
                component: <IsThisYou workHistoryData={workHistoryData} isLastStep={true} />,
                nextTitle: "wizard.step2.next.subStep0",
                backTitle: "wizard.step2.back.subStep0",
                nextStep: { step: 2, subStep: 0 },
                backStep: { step: 1, subStep: 1 },
            },
        ],
    ];

    return [
        {
            step,
            steps,
            shouldShowLoading,
            shouldDisableButton,
        },
        {
            handleMove,
            getFieldsValidation,
        },
    ];
};

export default useSetupWizard;
